import {
  AccountCircle,
  Code,
  Group,
  Logout,
  Payment,
  Hub,
} from '@mui/icons-material'
import {
  alpha,
  Box,
  darken,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import useSignOut from '~/features/auth/hooks/useSignOut'
import useUserAccess from '~/features/auth/hooks/useUserAccess'
import { isLibbyDomain } from '~/features/partners/utils'
import useGetUser from '~/features/user/hooks/useGetUser'
import useGetAuthSessionQuery from '~/hooks/useGetAuthSessionQuery'
import SelectAgent from '~/layouts/app/header/account/portal/selectAgent'
import { EAdminPortalClientPath, EClientPath } from '~/models/api'
import { ECognitoGroups } from '~/models/auth'
import EEnvironmentVariables from '~/models/config'
import { store } from '~/store'
import rem, { lightenOrDarken } from '~/utils/ui'

function AccountMenu() {
  const theme = useTheme()
  const { data: authSession } = useGetAuthSessionQuery()
  const { isPlusUser, isCustomAccess, canAccessOrgConfig, canManageUsers } =
    useUserAccess()
  const [isImpersonating, userId] = store((state) => [
    !!state.user.impersonatorsUserId,
    state.user.id,
  ])

  const { data: user } = useGetUser(isImpersonating ? userId : '')

  const email = isImpersonating ? user?.data?.email : authSession?.email
  const isLibby = isLibbyDomain()
  const navigate = useNavigate()
  const { signOut } = useSignOut()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const openMenuHandler = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenuHandler = () => {
    setAnchorEl(null)
  }

  const handleManageIntegrationsClick = () => {
    closeMenuHandler()
    navigate(EClientPath.manageIntegrations)
  }

  const isAdmin =
    !isImpersonating &&
    (authSession?.groups.includes(ECognitoGroups.admin) ||
      authSession?.groups.includes(ECognitoGroups.adminLimited))

  return (
    <>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={openMenuHandler}
        sx={{
          color: theme.palette.menuIcon,
          mr: { xxxs: 0, sm: 2 },
        }}
      >
        <AccountCircle fontSize="large" />
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClick={closeMenuHandler}
        onClose={closeMenuHandler}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              color: theme.palette.menuFont,
              backgroundColor: theme.palette.menuBackground,
              overflow: 'visible',
              overflowY: 'auto',
              filter: `drop-shadow(${rem(0)} ${rem(2)} ${rem(8)} ${
                theme.palette.menuBackground
              })`,
              mt: 5,
              '& .MuiMenuItem-root': {
                '& .MuiListItemIcon-root > svg': {
                  color: theme.palette.menuIcon,
                  transition: 'all 0.3s ease',
                },
                '&:hover .MuiListItemIcon-root > svg': {
                  color: darken(theme.palette.menuIcon, 0.4),
                },
                paddingInline: 4,
                paddingBlock: 1,
                '& +.MuiDivider-root': {
                  marginBlock: rem(4),
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.menuFont,
                },
                '&:not(.label):hover': {
                  backgroundColor: alpha(theme.palette.mainBackground, 0.2),
                },
                '&.label': {
                  color: lightenOrDarken(theme.palette.menuFont),
                  letterSpacing: rem(0.7),
                  fontSize: rem(14),
                  '&:hover': {
                    backgroundColor: theme.palette.menuBackground,
                    cursor: 'default',
                  },
                },
                '&.MuiButtonBase-root.Mui-disabled': {
                  opacity: 1,
                },
              },
            },
          },
        }}
      >
        <MenuItem disabled>
          <ListItemIcon>
            <AccountCircle fontSize="medium" />
          </ListItemIcon>
          {email}
        </MenuItem>

        <Divider
          sx={{ backgroundColor: alpha(theme.palette.mainBackground, 0.5) }}
          variant="fullWidth"
        />

        {import.meta.env[EEnvironmentVariables.VITE_IS_MULTIAGENT_ENABLED] ===
          'true' && <SelectAgent shouldShowOnSmallScreen />}
        <Divider
          sx={{
            backgroundColor: alpha(theme.palette.mainBackground, 0.5),
            display: { sm: 'none' },
          }}
          variant="fullWidth"
        />

        {(!isCustomAccess || canManageUsers || canAccessOrgConfig) && (
          <MenuItem className="label">Account</MenuItem>
        )}

        {(!isCustomAccess || canManageUsers) && (
          <MenuItem onClick={() => navigate(EClientPath.users)}>
            <ListItemIcon>
              <Group fontSize="small" />
            </ListItemIcon>
            <ListItemText>Manage Team</ListItemText>
          </MenuItem>
        )}

        {canAccessOrgConfig && (
          <MenuItem
            onClick={() =>
              navigate(isLibby ? EClientPath.billing : EClientPath.account)
            }
          >
            <ListItemIcon>
              <Payment fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {isLibby ? 'Plan & Billing' : 'Account'}
            </ListItemText>
          </MenuItem>
        )}

        {isPlusUser &&
          canAccessOrgConfig &&
          [
            <Divider
              key="integrations-divider"
              sx={{ backgroundColor: alpha(theme.palette.mainBackground, 0.5) }}
              variant="fullWidth"
            />,
            <MenuItem className="label" key="integrations-label">
              Integrations
            </MenuItem>,
            <MenuItem
              key="manage-integrations"
              onClick={() => handleManageIntegrationsClick()}
              sx={{ padding: 0 }}
            >
              <ListItemIcon>
                <Hub fontSize="small" />
              </ListItemIcon>
              Connected Apps
            </MenuItem>,
            <MenuItem key="api-keys">
              <ListItemIcon>
                <Code fontSize="small" />
              </ListItemIcon>
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
                onClick={() => navigate(EClientPath.settingsApiKeys)}
              >
                <ListItemText primary="API Keys" />
              </Box>
            </MenuItem>,
          ].map((item) => item)}

        {(!isCustomAccess || canManageUsers || canAccessOrgConfig) && (
          <Divider
            sx={{ backgroundColor: alpha(theme.palette.mainBackground, 0.5) }}
            variant="fullWidth"
          />
        )}

        {!isCustomAccess && isAdmin && (
          <MenuItem className="label">Portals</MenuItem>
        )}
        {!isCustomAccess && isAdmin && (
          <MenuItem onClick={() => navigate(EClientPath.overview)}>
            Customer Portal
          </MenuItem>
        )}
        {!isCustomAccess && isAdmin && (
          <MenuItem onClick={() => navigate(EAdminPortalClientPath.admin)}>
            Replify Admin Portal
          </MenuItem>
        )}

        {!isCustomAccess && isAdmin && (
          <Divider
            sx={{ backgroundColor: alpha(theme.palette.mainBackground, 0.5) }}
            variant="fullWidth"
          />
        )}

        <MenuItem
          onClick={() => {
            signOut().catch(() => undefined)
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  )
}

export default AccountMenu
